import { useTheme } from '@mui/material/styles';
import type { RouteComponentProps } from '@reach/router';
import { navigate } from 'gatsby';
import React, { useMemo } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { CurationStatusBadge } from '@components/CurationStatusBadge';
import { TableStatus, TableNote } from '@components/DataTable';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import NullStateDiv from '@components/NullStateDiv';
import { RecordSourcePill } from '@components/RecordSourcePill';
import SelfReportedPill from '@components/SelfReportedPill';
import {
  UpcomingAppointmentCard,
  UpcomingAppointmentGroup,
} from '@components/UpcomingAppointmentCard';

import * as ROUTES from '@constants/routes';

import { useGetProcedures } from '@hooks/api/procedure';
import { useSession } from '@hooks/useSession';

import { TableContainer, TableSpan } from '@layouts/Layouts/TableLayout';

import { splitRecordsByDate } from '@utils/format';

import type { CurationStatus, ResourceSourceType } from 'generated/types';

const messages = defineMessages({
  noPast: {
    id: 'surgeriesMain.noPast',
    defaultMessage: 'No Past Surgeries',
  },
  past: {
    id: 'surgeriesMain.past',
    defaultMessage: 'Past Surgeries',
  },
  upcoming: {
    id: 'surgeriesMain.upcoming',
    defaultMessage: 'UPCOMING',
  },
  title: {
    id: 'surgeriesMain.title',
    defaultMessage: 'Surgeries',
  },
});

const Surgeries: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();
  const {
    state: { isClient, isPractitioner, isVitall },
  } = useSession();

  const { data, loading } = useGetProcedures();
  const [pastSurgeries, upcomingSurgeries] = splitRecordsByDate(
    data,
    'procedureDate'
  );
  const { palette } = useTheme();

  const tableData = useMemo(
    () =>
      pastSurgeries.map((a) => ({
        id: a.id,
        viewedStatus: a.viewedStatus,
        procedureName: a.procedureName,
        hasNotes: Boolean(a.recordNotes.length),
        facilityCity: a.address,
        hcpName: a.practitioner,
        facilityName: a.facility,
        procedureDate: a.getProcedureDateStr(),
        selfReported: a.selfReported,
        curationStatus: a.curationStatus,
        recordSource: a.recordSource,
      })),
    [pastSurgeries]
  );

  return (
    <ContentLoader loading={loading}>
      <TableContainer>
        {upcomingSurgeries.length > 0 && (
          <>
            <TableSpan>
              <h2>{formatMessage(messages.upcoming)}</h2>
            </TableSpan>
            <UpcomingAppointmentGroup>
              {upcomingSurgeries.map((s) => (
                <UpcomingAppointmentCard
                  colour={palette.primary.main}
                  date={s.getProcedureDate()}
                  key={s.id}
                  onClick={() => {
                    navigate(`${ROUTES.SURGERIES}${s.id}`);
                  }}
                  subtitle={s.facility}
                  title={s.procedureName}
                />
              ))}
            </UpcomingAppointmentGroup>
          </>
        )}

        {pastSurgeries.length === 0 ? (
          <>
            <TableSpan>
              <h2>{formatMessage(messages.past)}</h2>
            </TableSpan>
            <NullStateDiv>{formatMessage(messages.noPast)}</NullStateDiv>
          </>
        ) : (
          <MuiDataTable
            columns={[
              {
                name: 'viewedStatus',
                options: {
                  customHeadLabelRender: () => null,
                  customBodyRender: (value: boolean) => (
                    <TableStatus value={value} />
                  ),
                },
              },
              {
                name: 'curationStatus',
                label: 'Curation Status',
                className: 'small',
                options: {
                  customBodyRender: (status: CurationStatus) => (
                    <CurationStatusBadge status={status} />
                  ),
                  filter: isVitall,
                  filterType: 'multiselect',
                  display: isVitall,
                },
              },
              {
                name: 'recordSource',
                label: 'Record Source',
                className: 'small',
                options: {
                  customBodyRender: (source: ResourceSourceType) => (
                    <RecordSourcePill type={source} />
                  ),
                  filter: isVitall || isPractitioner,
                  filterType: 'multiselect',
                  display: isVitall || isPractitioner,
                },
              },
              {
                name: 'procedureName',
                label: 'Procedure Name',
                className: 'blue-hover',
                options: { filter: true, sort: true, searchable: true },
              },
              {
                name: 'hasNotes',
                desktopOnly: true,
                options: {
                  customHeadLabelRender: () => null,
                  customBodyRender: (value: boolean) => (
                    <TableNote value={value} />
                  ),
                },
              },
              {
                name: 'facilityName',
                label: 'Facility Name',
                className: 'small',
                desktopOnly: true,
                options: { filter: true, sort: true, searchable: true },
              },
              {
                name: 'hcpName',
                label: 'Provider Name',
                className: 'small',
                options: { filter: true, sort: true, searchable: true },
              },
              {
                name: 'facilityCity',
                label: 'Facility City',
                className: 'small-light',
                options: { filter: true, sort: true, searchable: true },
              },
              {
                name: 'procedureDate',
                className: 'date',
                label: 'Procedure Date',
                options: { filter: true, sort: true },
              },
              {
                name: 'selfReported',
                options: {
                  customHeadLabelRender: () => null,
                  customBodyRender: (value: boolean) => (
                    <SelfReportedPill value={value} />
                  ),
                  display: isClient,
                  filter: isClient,
                },
              },
            ]}
            data={tableData}
            options={{
              onRowClick: (_rowData, { dataIndex }) => {
                if (dataIndex <= tableData.length - 1) {
                  const row = tableData[dataIndex];
                  navigate(`${ROUTES.SURGERIES}${row.id}`);
                }
              },
              filter: true,
              sort: true,
              search: true,
              sortOrder: {
                name: 'procedureDate',
                direction: 'desc',
              },
            }}
            pageParam="surgeries"
            title={formatMessage(messages.past)}
          />
        )}
      </TableContainer>
    </ContentLoader>
  );
};

export default withErrorBoundary(Surgeries, {
  FallbackComponent: ErrorBoundaryFallback,
});
